import React, { useRef, useState, useEffect, useCallback } from 'react';

interface DualScrollWrapperProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * DualScrollWrapper - Component that provides synchronized horizontal scrollbars
 * at both top and bottom of a table for better UX when scrolling wide tables.
 */
const DualScrollWrapper: React.FC<DualScrollWrapperProps> = ({
  children,
  className = ''
}) => {
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [showTopScroll, setShowTopScroll] = useState(false);

  // Update content width for top scrollbar
  useEffect(() => {
    const updateContentWidth = () => {
      if (contentRef.current) {
        const scrollWidth = contentRef.current.scrollWidth;
        const clientWidth = contentRef.current.clientWidth;
        setContentWidth(scrollWidth);
        // Only show top scrollbar if content overflows
        setShowTopScroll(scrollWidth > clientWidth);
      }
    };

    updateContentWidth();

    // Use ResizeObserver to track content width changes
    const resizeObserver = new ResizeObserver(updateContentWidth);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    // Also listen to window resize
    window.addEventListener('resize', updateContentWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateContentWidth);
    };
  }, [children]);

  // Scroll sync handlers
  const handleTopScroll = useCallback(() => {
    if (isScrolling) return;
    setIsScrolling(true);
    if (bottomScrollRef.current && topScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => setIsScrolling(false));
  }, [isScrolling]);

  const handleBottomScroll = useCallback(() => {
    if (isScrolling) return;
    setIsScrolling(true);
    if (topScrollRef.current && bottomScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => setIsScrolling(false));
  }, [isScrolling]);

  return (
    <div className={`dual-scroll-wrapper flex flex-col overflow-hidden ${className}`}>
      {/* Top Scrollbar - only show when content overflows */}
      {showTopScroll && (
        <div
          ref={topScrollRef}
          onScroll={handleTopScroll}
          className="overflow-x-auto overflow-y-hidden border-b border-secondary-200 bg-secondary-50 rounded-t-lg flex-shrink-0"
          style={{ height: '12px' }}
        >
          <div style={{ width: contentWidth, height: '1px' }} />
        </div>
      )}

      {/* Content with Bottom Scrollbar */}
      <div
        ref={(el) => {
          (bottomScrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }}
        onScroll={handleBottomScroll}
        tabIndex={0}
        role="region"
        aria-label="พื้นที่ตารางที่เลื่อนได้"
        className={`overflow-x-auto overflow-y-auto min-h-0 flex-1 ${showTopScroll ? 'rounded-b-lg' : 'rounded-lg'}`}
      >
        {children}
      </div>
    </div>
  );
};

export default DualScrollWrapper;
